"use client"

import { useAutoleadsLocale } from "../lib/locale-context"
import { useTr } from "../lib/use-tr"
import { AlarmClock } from "lucide-react"
import type { ReactNode } from "react"

import {
  businessDaysUntil,
  dueLevel,
  dueTone,
  formatDue,
} from "@/lib/apps/autoleads/task-deadline"
import { cn } from "@/lib/utils"

import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const DUE_DATE_T = {
  dueToday: { FR: "Aujourd'hui", EN: "Today" },
  dueWeekend: { FR: "Ce week-end", EN: "This weekend" },
  dueInOne: { FR: "Dans 1 jour ouvré", EN: "In 1 business day" },
  dueInMany: { FR: "Dans {n} jours ouvrés", EN: "In {n} business days" },
  dueAgoOne: { FR: "Il y a 1 jour ouvré", EN: "1 business day ago" },
  dueAgoMany: { FR: "Il y a {n} jours ouvrés", EN: "{n} business days ago" },
}

// How far the date is, in working days: what the rep plans with.
export function useBusinessDaysLabel() {
  const tr = useTr(DUE_DATE_T)
  return (due: string) => {
    const days = businessDaysUntil(due)
    if (days === 0) {
      const sameDay = new Date(due).toDateString() === new Date().toDateString()
      return tr(sameDay ? "dueToday" : "dueWeekend")
    }
    const n = Math.abs(days)
    if (days > 0)
      return n === 1
        ? tr("dueInOne")
        : tr("dueInMany").replace("{n}", String(n))
    return n === 1
      ? tr("dueAgoOne")
      : tr("dueAgoMany").replace("{n}", String(n))
  }
}

// A task's deadline, tinted by how close it is. Hovering it says how many
// business days are left (or gone). `tooltip={false}` when the badge already
// sits inside another tooltip's trigger: the caller shows the label there.
export function DueDateBadge({
  due,
  overdue,
  tooltip = true,
  className,
  children,
}: {
  due: string
  overdue: boolean
  tooltip?: boolean
  className?: string
  children?: ReactNode
}) {
  const locale = useAutoleadsLocale()
  const businessDaysLabel = useBusinessDaysLabel()
  const level = dueLevel(due, overdue)
  const badge = (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 font-normal tabular-nums",
        dueTone({ level }),
        className,
      )}
    >
      <AlarmClock className="size-3" />
      {formatDue(due, locale)}
      {children}
    </Badge>
  )
  if (!tooltip) return badge
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {businessDaysLabel(due)}
      </TooltipContent>
    </Tooltip>
  )
}
